import { UserManager as LegacyUserManager } from 'oidc-client'
import {
    UserManager as SidUserManager,
    type SigninSilentArgs,
    type User,
    type UserManagerSettings,
    WebStorageStateStore,
} from 'oidc-client-ts'

import { Environment } from 'Common/environment/types'

import { AuthMode, resolveIdentityConfig } from './identityConfig'

export type AuthClient = {
    mode: AuthMode
    userManager: LegacyUserManager | SidUserManager
}

const REFRESH_LOCK = 'mockingbird-sid-token-refresh'

const issuedAt = (user: User | null): number => user?.expires_at ?? 0

class CrossTabUserManager extends SidUserManager {
    override async signinSilent(args?: SigninSilentArgs): Promise<User | null> {
        if (!navigator.locks) return super.signinSilent(args)

        const before = await this.getUser()

        return navigator.locks.request(REFRESH_LOCK, async () => {
            const stored = await this.getUser()
            const refreshedWhileWaiting =
                stored && !stored.expired && issuedAt(stored) > issuedAt(before)

            if (refreshedWhileWaiting) {
                return this.getUser(true)
            }
            return super.signinSilent(args)
        })
    }
}

export function createAuthClient(environment: Environment): AuthClient {
    const config = resolveIdentityConfig(environment)
    const origin = window.location.origin

    if (config.mode === 'sid') {
        const settings: UserManagerSettings = {
            authority: config.authority,
            client_id: config.clientId,
            redirect_uri: origin,
            post_logout_redirect_uri: origin,
            response_type: 'code',
            scope: config.scope,
            loadUserInfo: true,
            automaticSilentRenew: true,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            ...(config.customerHint
                ? {
                      extraQueryParams: { CUSTOMER_HINT: config.customerHint },
                      extraTokenParams: { CUSTOMER_HINT: config.customerHint },
                  }
                : {}),
        }

        return { mode: 'sid', userManager: new CrossTabUserManager(settings) }
    }

    const userManager = new LegacyUserManager({
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: origin,
        silent_redirect_uri: origin + '/silent_renew.html',
        post_logout_redirect_uri: '/services/auth/manage',
        response_type: 'id_token token',
        scope: config.scope,
        automaticSilentRenew: true,
        filterProtocolClaims: true,
        loadUserInfo: true,
    })
    return { mode: 'legacy', userManager }
}
